/**
 * Modal de verificación TOTP durante el sign-in.
 *
 * Se abre cuando el reducer authenticate queda en AUTH.MFA_TOTP_REQUIRED
 * (signInWithEmailAndPassword lanzó auth/multi-factor-auth-required). El
 * usuario ingresa el código de su app autenticadora y el resolver —
 * guardado en application/lib/mfa_resolver.js — completa el sign-in, lo
 * que dispara onAuthStateChanged y el Router retoma el flujo normal.
 */

import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Input, Modal, Button, notification } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';

import { AUTH } from '../../../application/types';
import { mfa } from '../../actions/auth/mfa';
import { clearMfaResolver } from '../../lib/mfa_resolver';
import { ModalResetTotp } from '../mfa/reset_totp';

const FormItem = Form.Item;

export const ModalTotp = () => {

    const dispatch = useDispatch();
    const auth = useSelector(state => state.get('authenticate').get('login'));

    const [loading, setLoading] = useState(false);
    const [resetVisible, setResetVisible] = useState(false);
    const [form] = Form.useForm();

    const visible = auth?.type === AUTH.MFA_TOTP_REQUIRED;

    // Vuelve al formulario de login descartando el desafío pendiente
    const onCancel = () => {
        clearMfaResolver();
        form.resetFields();
        dispatch({ type: AUTH.LOGGINGIN });
    };

    const onVerify = async values => {
        setLoading(true);
        try {
            // En éxito dispara onAuthStateChanged → el Router continúa;
            // este modal se cierra solo porque auth.type deja de ser
            // MFA_TOTP_REQUIRED.
            await dispatch( mfa.resolveTotpSignIn( values.code ) );
            form.resetFields();
        } catch ( error ) {
            if ( error.code === 'auth/invalid-verification-code' ) {
                notification.warning({
                    message: 'Código incorrecto',
                    description: 'Verifique el código de su app de autenticación e intente de nuevo',
                    placement: 'topRight'
                });
            } else {
                notification.error({
                    message: 'No se pudo verificar',
                    description: 'La verificación expiró. Inicie sesión nuevamente',
                    placement: 'topRight'
                });
                onCancel();
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal
                forceRender
                title = 'Verificación en 2 pasos'
                cancelText = 'Cancelar'
                okText = 'Verificar'
                maskClosable = { false }
                destroyOnHidden = { true }
                closable = { false }
                open = { visible }
                confirmLoading = { loading }
                onCancel = { onCancel }
                onOk = { () => form.submit() } >
                <Form form = { form } onFinish = { onVerify } >
                    <p> Ingrese el código de 6 dígitos de su app de autenticación (Google Authenticator, Microsoft Authenticator, etc.) </p>
                    <FormItem name = "code"
                        rules = {[
                            { required: true, message: 'Debe ingresar el código' },
                            { pattern: /^[0-9]{6}$/, message: 'El código tiene 6 dígitos' }
                        ]} >
                        <Input
                            autoFocus
                            maxLength = { 6 }
                            inputMode = "numeric"
                            placeholder = "000000"
                            prefix = { <SafetyOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> } />
                    </FormItem>
                    <Button type = "link" style = {{ padding: 0 }}
                        onClick = { () => setResetVisible(true) } >
                        Perdí el acceso a mi app de autenticación
                    </Button>
                </Form>
            </Modal>

            <ModalResetTotp visible = { resetVisible } setVisible = { setResetVisible } />
        </>
    )
}
